import type { RequestFormResponse } from "@/types/common/form";
import type {
  ApiFormalityFormResponseType,
  FORM_ASSIGNMENTS_STATUS,
  FormalityRequestType,
  FormDetailResponse,
} from "@/types/entities/formality-request";
import axios from "@/utils/axios";
import { parseStatusFilters } from "@/utils/filters";
import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { Params } from "react-router";

const FILTER_STATUS = "filter[status]" as const;
const FILTER_IS_EXPRESS = "filter[is_express]" as const;

interface GetFormalityRequestFilters {
  search?: string;
  page?: number;
  per_page?: number;
  order_by?: string;
  group?: string;
  assigned?: string;
  [FILTER_STATUS]?: string;
  [FILTER_IS_EXPRESS]?: string;
}
export const useGetFormalityRequests = (
  props: GetFormalityRequestFilters,
  refreshInterval = 0,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["formaly-requests", props],
    queryFn: async () => {
      const {
        group,
        assigned,
        [FILTER_STATUS]: filterStatus,
        [FILTER_IS_EXPRESS]: filterIsExpress,
        ...rest
      } = props;

      const { statusFilter, suspensionStatusFilter } =
        parseStatusFilters(filterStatus);

      const response = await axios.get("/admin/v1/formality-request", {
        params: {
          ...rest,
          ...(group ? { "filter[group]": group } : {}),
          ...(assigned ? { "filter[assigned]": assigned } : {}),
          ...(filterIsExpress ? { "filter[is_express]": filterIsExpress } : {}),
          ...statusFilter,
          ...suspensionStatusFilter,
          relationships:
            "process,assignedTo,flowExecution.flow,groups,currentNode,flowExecution.activeNodeExecution.revision.corrections",
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled, // Solo ejecutar si enabled es true
  });
};

const getFormalityRequest = async (
  id: string,
): Promise<FormalityRequestType> => {
  const response = await axios.get(`/admin/v1/formality-request/${id}`, {
    params: {
      relationships:
        "process,assignedTo,process.tags,groups,flowExecution,applicant,flowExecution.activeNodeExecution.revision.corrections",
    },
  });
  return response.data.data;
};

export const useGetFormalityRequestDetail = (id: string) => {
  return useQuery({
    queryKey: ["formaly-request", id],
    queryFn: async () => {
      return await getFormalityRequest(id);
    },
    enabled: !!id, // Solo ejecutar si hay id
    refetchInterval: 5000, // Refrescar cada 5 segundos
  });
};

export const useGetFormalityRequestLogs = (
  id: string,
  params?: {
    page?: number;
    per_page?: number;
    order_by?: string;
    grouped?: number;
  },
) => {
  return useQuery({
    queryKey: ["formaly-request-logs", id, params],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/formality-request/${id}/logs`,
        {
          params: {
            page: params?.page ?? 1,
            per_page: params?.per_page ?? 15,
            order_by: params?.order_by ?? "!created_at",
            grouped: params?.grouped ?? 0,
          },
        },
      );
      return response.data;
    },
    enabled: !!id, // Solo ejecutar si hay id
    refetchInterval: 5000, // Refrescar cada 5 segundos
  });
};

export const createFormalityRequestBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    try {
      const formality = await queryClient.ensureQueryData({
        queryKey: ["formaly-request", id],
        queryFn: async () => {
          const response = await getFormalityRequest(id);
          return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          name: formality.code,
        },
      };
    } catch (error) {
      console.log("Error fetching role for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetTramiteForms = (
  tramiteId: string,
  params: {
    page: number;
    per_page: number;
  },
  refreshInterval = 0,
) => {
  return useQuery<ApiFormalityFormResponseType>({
    queryKey: ["tramite-forms", tramiteId, params],
    queryFn: async (): Promise<ApiFormalityFormResponseType> => {
      const response = await axios.get(`/admin/v1/forms`, {
        params: {
          ...params,
          "filter[formality]": tramiteId,
          relationships: "formAssignments",
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled: !!tramiteId, // Solo ejecutar si hay tramiteId
  });
};

const getFormDetails = async (
  id: string,
  formality: string,
): Promise<FormDetailResponse> => {
  const response = await axios.get(`/admin/v1/forms/${id}`, {
    params: {
      relationships: "steps.sections.fields.filledFields,formAssignments",
      "filter[formality]": formality,
    },
  });
  return response.data.data;
};

export const useGetFormDetail = (formId: string, formality: string) => {
  return useQuery<FormDetailResponse>({
    queryKey: ["form-detail", formId],
    queryFn: async (): Promise<FormDetailResponse> => {
      return await getFormDetails(formId, formality);
    },
    enabled: !!formId && !!formality,
  });
};

//* This should not exist, but there is not way to apply eager loading without refactoring all Form API endpoints with Frontend integrations
export const useGetFormDetail2 = (formId: string, formality: string) => {
  return useQuery<FormDetailResponse>({
    queryKey: ["form-detail", formId],
    queryFn: async (): Promise<FormDetailResponse> => {
      const response = await axios.get(`/admin/v1/forms/${formId}/v2`, {
        params: {
          relationships:
            "steps.sections.fields.filledFields,formAssignments,userFormAssignments,userFormAssignments.user,userFormAssignments.filledForm,userFormAssignments.form,formAssignments.revisions.corrections.fieldCorrections.filledField",
          "filter[formality]": formality,
        },
      });
      return response.data.data;
    },
    enabled: !!formId && !!formality,
  });
};

// Para cargar variables de otros nodos
export const useGetFormByAssignmentId = (assignmentId: string) => {
  return useQuery({
    queryKey: ["get-form-by-assignment-id", assignmentId],
    queryFn: async (): Promise<RequestFormResponse> => {
      const { data } = await axios.get(
        `/admin/v1/internal/forms/assignments/${assignmentId}`,
      );
      return {
        data: data.data,
      };
    },
    enabled: !!assignmentId && assignmentId !== "",
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const createGetFormDetailBreadcrumb = (
  queryClient: QueryClient,
  formId: string,
  formality: string,
  params?: Params<string>,
) => {
  return async () => {
    try {
      const formDetail = await queryClient.ensureQueryData({
        queryKey: ["form-detail", formId],
        queryFn: async () => {
          const response = await getFormDetails(formId, formality);
          return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          name: formDetail.name,
          params,
        },
      };
    } catch (error) {
      console.log("Error fetching form for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useUpdateFormalityRequestAssignee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["update-formality-request-assignee"],
    mutationFn: async ({
      id,
      assignments,
      reason_text,
      reason_id,
    }: {
      id: string;
      assignments: Array<{ group_id: string; assigned_to: string }>;
      reason_text: string;
      reason_id: string;
    }) => {
      const response = await axios.put(
        `/admin/v1/formality-request/${id}/groups/reassign`,
        {
          assignments,
          reason_text,
          reason_id,
        },
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["formaly-requests"] });
      queryClient.invalidateQueries({
        queryKey: ["formaly-request", variables.id],
      });
    },
  });
};

export const useUpdateRevision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-revision"],
    mutationFn: async (data: {
      revision_id: string;
      revision_comment?: string;
      correction_comment?: string;
      group_id?: string;
      correction_fields: Array<{
        filled_field_id: string;
        status: "APPROVED" | "DENIED" | "IN_PROCESS";
        comment?: string;
      }>;
    }) => {
      const response = await axios.put(
        `/admin/v1/formality-request/revision`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate form-detail queries to ensure fresh data on navigation
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
    },
  });
};

export const useLeaderApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["leader-approval"],
    mutationFn: async (data: {
      correction_id: string;
      approved: boolean;
      comments?: string;
    }) => {
      const response = await axios.post(
        `/admin/v1/formality-request/revision/corrections/leader-approval`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate form-detail and revisions queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["revisions"] });
    },
  });
};

// -------------- Historial de aprobaciones ---------------------

// Tipos base
export type Status = "APPROVED" | "IN_PROCESS" | "DENIED" | string;

export type Person = {
  id: string | null;
  name: string | null;
  last_name: string | null;
  email?: string | null;
  phone_number?: string | null;
  dui?: string | null;
  is_blocked?: boolean | null;
  identification_type?: string | null;
  keycloak_id?: string | null;
  user_status?: string | null;
  institution?: { id: string; name: string } | null;
  department?: { id: string; name: string } | null;
  position?: { id: string; name: string } | null;
  updated_at?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
};

export type Comment = {
  id: string;
  text: string;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  creator?: Person | null;
};

export type Correction = {
  id: string;
  revision_id: string;
  status: Status;
  is_leader_approved: boolean;
  created_by_id: string | null;
  created_by?: Person | null;
  creator?: Person | null;
  comments: Comment[];
  created_at: string | null;
  updated_at: string | null;
  group?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
  field_corrections?: Array<{
    id: string;
    correction_id: string;
    filled_field_id: string;
    status: string;
    comments: string | null;
    created_at: string | null;
    updated_at: string | null;
  }>;
};

export type DeniedCorrection = {
  id: string;
  revision_id: string;
  status: Status;
  is_leader_approved: boolean;
  created_by_id: string | null;
  created_by?: Person | null;
  creator?: Person | null;
  comments: Comment[];
  created_at: string | null;
  updated_at: string | null;
  group?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
};

export type Revision = {
  id: string;
  status: Status;
  requires_leader_approval: boolean;
  applicant_form_assignment_id?: string | null;
  applicant_form_assignment?: {
    id: string;
    status: keyof typeof FORM_ASSIGNMENTS_STATUS;
    applicant?: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      document_number?: string;
    };
  } | null;
  created_by?: string | null;
  created_at: string | null;
  updated_at: string | null;
  corrections: Correction[];
  denied_corrections?: DeniedCorrection[];
  groups?: Array<{
    id: string;
    name: string;
    description?: string | null;
  }>;
};

export type FilterValue =
  | string
  | number
  | boolean
  | (string | number | boolean)[];
export type Filters = Record<string, FilterValue>;

export type FormalityRevisionApiItem = {
  id: string | number;
  status: Status;
  requires_leader_approval?: boolean;
  applicant_form_assignment_id?: string | number | null;
  applicant_form_assignment?: {
    id: string | number;
    status: keyof typeof FORM_ASSIGNMENTS_STATUS;
    applicant?: {
      id: string | number;
      first_name?: string;
      last_name?: string;
      email?: string;
      document_number?: string;
    };
  } | null;
  created_by?: string | number | null;
  updated_at?: Date | string | null;
  created_at?: Date | string | null;
  groups?: Array<{
    id: string | number;
    name: string;
    description?: string | null;
  }> | null;
  corrections?: Array<{
    id: string | number;
    revision_id?: string | number;
    status: Status;
    is_leader_approved?: boolean;
    created_by_id?: string | number | null;
    created_by?: Person | null;
    created_at?: Date | string | null;
    updated_at?: Date | string | null;
    group?: {
      id: string | number;
      name: string;
      description?: string | null;
    } | null;
    field_corrections?: Array<{
      id: string | number;
      correction_id: string | number;
      filled_field_id: string | number;
      status: string;
      comments?: string | null;
      created_at?: Date | string | null;
      updated_at?: Date | string | null;
    }> | null;
    comments?: Array<{
      id: string | number;
      text?: string;
      created_by?: string | number | null;
      created_at?: Date | string | null;
      updated_at?: Date | string | null;
      creator?: Person | null;
    }> | null;
  }> | null;
  denied_corrections?: Array<{
    id: string | number;
    revision_id?: string | number;
    status: Status;
    is_leader_approved?: boolean;
    created_by_id?: string | number | null;
    created_by?: Person | null;
    created_at?: Date | string | null;
    updated_at?: Date | string | null;
    group?: {
      id: string | number;
      name: string;
      description?: string | null;
    } | null;
    comments?: Array<{
      id: string | number;
      text?: string;
      created_by?: string | number | null;
      created_at?: Date | string | null;
      updated_at?: Date | string | null;
      creator?: Person | null;
    }> | null;
  }> | null;
};

export type FormalityRevisionApiResponse =
  | FormalityRevisionApiItem[]
  | { data: FormalityRevisionApiItem[] };

const s = (v: unknown): string => (v == null ? "" : String(v));
const iso = (v: unknown): string | null =>
  v == null ? null : v instanceof Date ? v.toISOString() : String(v);
const arr = <T>(v?: T[] | null): T[] => (Array.isArray(v) ? v : []);

function normalizeRevision(item: FormalityRevisionApiItem): Revision {
  const corrections: Correction[] = arr(item.corrections).map((c) => ({
    id: s(c.id),
    revision_id: s(c.revision_id ?? item.id),
    status: c.status,
    is_leader_approved: c.is_leader_approved ?? false,
    created_by_id: c.created_by_id == null ? null : s(c.created_by_id),
    created_by: c.created_by ?? null,
    creator: c.created_by ?? null,
    created_at: iso(c.created_at),
    updated_at: iso(c.updated_at),
    group: c.group
      ? {
          id: s(c.group.id),
          name: c.group.name,
          description: c.group.description ?? null,
        }
      : undefined,
    field_corrections: arr(c.field_corrections).map((fc) => ({
      id: s(fc.id),
      correction_id: s(fc.correction_id),
      filled_field_id: s(fc.filled_field_id),
      status: fc.status,
      comments: fc.comments ?? null,
      created_at: iso(fc.created_at),
      updated_at: iso(fc.updated_at),
    })),
    comments: arr(c.comments).map((m) => ({
      id: s(m.id),
      text: m.text ?? "",
      created_by: m.created_by == null ? null : s(m.created_by),
      created_at: iso(m.created_at),
      updated_at: iso(m.updated_at),
      creator: m.creator ?? null,
    })),
  }));

  const groups = arr(item.groups).map((g) => ({
    id: s(g.id),
    name: g.name,
    description: g.description ?? null,
  }));

  return {
    id: s(item.id),
    status: item?.applicant_form_assignment?.status || item.status,
    requires_leader_approval: item.requires_leader_approval ?? false,
    applicant_form_assignment_id:
      item.applicant_form_assignment_id == null
        ? null
        : s(item.applicant_form_assignment_id),
    applicant_form_assignment: item.applicant_form_assignment
      ? {
          id: s(item.applicant_form_assignment.id),
          status: item.applicant_form_assignment.status,
          applicant: item.applicant_form_assignment.applicant
            ? {
                id: s(item.applicant_form_assignment.applicant.id),
                first_name: item.applicant_form_assignment.applicant.first_name,
                last_name: item.applicant_form_assignment.applicant.last_name,
                email: item.applicant_form_assignment.applicant.email,
                document_number:
                  item.applicant_form_assignment.applicant.document_number,
              }
            : undefined,
        }
      : null,
    created_by: item.created_by == null ? null : s(item.created_by),
    created_at: iso(item.created_at),
    updated_at: iso(item.updated_at),
    corrections,
    denied_corrections: arr(item.denied_corrections).map((dc) => ({
      id: s(dc.id),
      revision_id: s(dc.revision_id ?? item.id),
      status: dc.status,
      is_leader_approved: dc.is_leader_approved ?? false,
      created_by_id: dc.created_by_id == null ? null : s(dc.created_by_id),
      created_by: dc.created_by ?? null,
      creator: dc.created_by ?? null,
      created_at: iso(dc.created_at),
      updated_at: iso(dc.updated_at),
      group: dc.group
        ? {
            id: s(dc.group.id),
            name: dc.group.name,
            description: dc.group.description ?? null,
          }
        : undefined,
      comments: arr(dc.comments).map((m) => ({
        id: s(m.id),
        text: m.text ?? "",
        created_by: m.created_by == null ? null : s(m.created_by),
        created_at: iso(m.created_at),
        updated_at: iso(m.updated_at),
        creator: m.creator ?? null,
      })),
    })),
    groups: groups.length > 0 ? groups : undefined,
  };
}

function adaptToRevisions(payload: FormalityRevisionApiResponse): Revision[] {
  const list: FormalityRevisionApiItem[] = Array.isArray(payload)
    ? payload
    : (payload?.data ?? []);
  return list.map(normalizeRevision);
}

// Query params
function buildSearchParams(params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): URLSearchParams {
  const sp = new URLSearchParams();
  sp.append("filter[formality_request_id]", params.formality_request_id);

  // Filtros
  const entries = Object.entries(params.filters ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, raw] of entries) {
    if (raw == null) continue;
    const push = (v: string | number | boolean) =>
      sp.append(`filter[${key}]`, String(v));
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        if (v != null) push(v);
      });
    } else {
      push(raw);
    }
  }

  if (params.relationships?.length)
    sp.set("relationships", params.relationships.join(","));

  if (params.page != null) sp.set("page", String(params.page));
  if (params.per_page != null) sp.set("per_page", String(params.per_page));
  if (params.order_by != null) sp.set("order_by", String(params.order_by));
  if (params.order_direction != null)
    sp.set("order_direction", String(params.order_direction));

  return sp;
}

// API
export const getFormalityRequestRevision = async (params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): Promise<Revision[]> => {
  const searchParams = buildSearchParams(params);
  const { data } = await axios.get(
    `/admin/v1/formality-request/revision?${searchParams.toString()}`,
  );
  return adaptToRevisions(data);
};

export const getFormalityRequestRevisionExpired = async (
  formality_request_id: string,
): Promise<Revision[]> => {
  const { data } = await axios.get(
    `/admin/v1/formality-request/${formality_request_id}/expired-revisions`,
  );
  return adaptToRevisions(data);
};

// Hook
export const useGetFormalityRequestRevision = (params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}) => {
  const key = [
    "formality-request-revision",
    params.formality_request_id,
    params.relationships?.slice().sort().join(",") ?? "",
    JSON.stringify(
      Object.fromEntries(
        Object.entries(params.filters ?? {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      ),
    ),
    params.page ?? null,
    params.per_page ?? null,
    params.order_by ?? null,
    params.order_direction ?? null,
  ];

  return useQuery<Revision[], unknown, Revision[]>({
    queryKey: key,
    queryFn: () => getFormalityRequestRevision(params),
    enabled: !!params.formality_request_id,
  });
};

// New endpoint: /admin/v1/revisions
export const getRevisions = async (params: {
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): Promise<Revision[]> => {
  const sp = new URLSearchParams();

  // Filtros
  const entries = Object.entries(params.filters ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, raw] of entries) {
    if (raw == null) continue;
    const push = (v: string | number | boolean) =>
      sp.append(`filter[${key}]`, String(v));
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        if (v != null) push(v);
      });
    } else {
      push(raw);
    }
  }

  if (params.relationships?.length)
    sp.set("relationships", params.relationships.join(","));

  if (params.page != null) sp.set("page", String(params.page));
  if (params.per_page != null) sp.set("per_page", String(params.per_page));
  if (params.order_by != null) sp.set("order_by", String(params.order_by));
  if (params.order_direction != null)
    sp.set("order_direction", String(params.order_direction));

  const { data } = await axios.get(`/admin/v1/revisions?${sp.toString()}`);
  return adaptToRevisions(data);
};

export const useGetRevisions = (params: {
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
  enabled?: boolean;
}) => {
  const { enabled = true, ...restParams } = params;
  const key = [
    "revisions",
    restParams.relationships?.slice().sort().join(",") ?? "",
    JSON.stringify(
      Object.fromEntries(
        Object.entries(restParams.filters ?? {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      ),
    ),
    restParams.page ?? null,
    restParams.per_page ?? null,
    restParams.order_by ?? null,
    restParams.order_direction ?? null,
  ];

  return useQuery<Revision[], unknown, Revision[]>({
    queryKey: key,
    queryFn: () => getRevisions(restParams),
    enabled,
  });
};
export interface CreateCancelRequestPayload {
  formality_request_id: string;
  reason: string;
}

export const useCreateCancelFormalityRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      formality_request_id,
      reason,
    }: CreateCancelRequestPayload) => {
      const response = await axios.post(
        `/admin/v1/formality-request/${formality_request_id}/cancellation-requests`,
        { reason },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["formaly-request"],
      });
      queryClient.invalidateQueries({
        queryKey: ["formaly-request"],
      });
    },
  });
};

export interface ApproveCancelRequestPayload {
  cancellation_request_id: string;
  approved: boolean;
}

export const useApproveCancelFormalityRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cancellation_request_id,
      approved,
    }: ApproveCancelRequestPayload) => {
      const response = await axios.post(
        `/admin/v1/formality-request/cancellation-requests/${cancellation_request_id}/leader-approval`,
        { approved },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["formaly-request"],
      });
      queryClient.invalidateQueries({
        queryKey: ["formaly-request"],
      });
    },
  });
};
